import React, { useEffect, Suspense, lazy, useState } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { SignedIn, SignedOut, RedirectToSignIn, useAuth } from "@clerk/clerk-react";
import { setClerkToken } from "@/integrations/supabase/client";
import { WeatherProvider } from "@/contexts/WeatherContext";
import { ClockProvider } from "@/contexts/ClockContext";
import { NotesManagerProvider } from "@/contexts/NotesManager";
import { OrganizationProvider } from "@/contexts/OrganizationContext";
import { AppsManagerProvider } from "@/contexts/AppsManager";
import { OptimizedContextProvider } from "@/contexts/OptimizedContextProvider";
import { NotificationManagerProvider } from "@/contexts/NotificationManager";
import { ChatWebSocketProvider } from "@/contexts/ChatWebSocketContext";
import { SoundstageWebSocketProvider } from "@/contexts/SoundstageWebSocketContext";
import { useFocusModeScheduler } from "@/hooks/useFocusModeScheduler";
import { DashboardWrapper } from "./components/dashboard/DashboardWrapper";
import Login from "./pages/Login";
import { DashboardSkeleton } from "./components/dashboard/DashboardSkeleton";

// Lazy load pages for better code splitting
const NotFound = lazy(() => import("./pages/NotFound"));
const Notifications = lazy(() => import("./pages/Notifications"));
const OnboardingProtectedRoute = lazy(() => import("./components/onboarding/OnboardingProtectedRoute").then(m => ({ default: m.OnboardingProtectedRoute })));
const Lock = lazy(() => import("./pages/Lock"));
const MeetingJoin = lazy(() => import("./pages/MeetingJoin"));
const GuestMeetingJoin = lazy(() => import("./pages/GuestMeetingJoin"));
const RoleManagement = lazy(() => import("./pages/admin/RoleManagement").then(m => ({ default: m.RoleManagement })));
const UserRoleManagement = lazy(() => import("./pages/admin/UserRoleManagement").then(m => ({ default: m.UserRoleManagement })));
const PermissionAuditLog = lazy(() => import("./pages/admin/PermissionAuditLog").then(m => ({ default: m.PermissionAuditLog })));

// Create QueryClient instance once outside component
// Optimized configuration for better performance and caching
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false, // Prevent unnecessary refetches
      refetchOnReconnect: 'always',
      retry: 3, // Retry failed requests
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
    },
    mutations: {
      retry: 1, // Retry mutations once
    },
  },
});

// Expose queryClient globally for cross-context invalidation (e.g., chat → soundstage)
(window as any).__queryClient = queryClient;

const DragDropGuard: React.FC = () => {
  useEffect(() => {
    const prevent = (e: DragEvent) => {
      // глобально глушим перетаскивание, чтобы браузер не открывал файл
      e.preventDefault();
      e.stopPropagation();
    };

    window.addEventListener("dragover", prevent);
    window.addEventListener("drop", prevent);

    return () => {
      window.removeEventListener("dragover", prevent);
      window.removeEventListener("drop", prevent);
    };
  }, []);

  return null;
};

const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

// Component for routes without Clerk
const AppWithoutClerk = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <BrowserRouter>
        <DragDropGuard />
        <WeatherProvider>
          <ClockProvider>
            <NotesManagerProvider>
              <AppsManagerProvider>
                <NotificationManagerProvider>
                  <OptimizedContextProvider>
                  <Toaster />
                  <Sonner />
                <Routes>
                  <Route path="/login" element={<Login />} />
                  <Route path="*" element={<Login />} />
                </Routes>
                  </OptimizedContextProvider>
                </NotificationManagerProvider>
              </AppsManagerProvider>
            </NotesManagerProvider>
          </ClockProvider>
        </WeatherProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

// Component to sync Clerk JWT with Supabase
const ClerkSupabaseSync: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { getToken, isSignedIn, isLoaded } = useAuth();
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    let isCancelled = false;
    let interval: ReturnType<typeof setInterval> | null = null;

    const syncToken = async () => {
      if (!isLoaded) return;

      if (isSignedIn) {
        await setClerkToken(getToken);
        if (!isCancelled) {
          setIsReady(true);
        }

        interval = setInterval(() => {
          setClerkToken(getToken);
        }, 5 * 60 * 1000);
      } else {
        await setClerkToken(null);
        if (!isCancelled) {
          setIsReady(true);
        }
      }
    };

    setIsReady(false);
    syncToken();

    return () => {
      isCancelled = true;
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [getToken, isSignedIn, isLoaded]);

  if (!isLoaded || (isSignedIn && !isReady)) {
    return <DashboardSkeleton />;
  }

  return <>{children}</>;
};

// Component to run focus mode scheduler
const FocusModeSchedulerService = () => {
  useFocusModeScheduler();
  return null;
};

// Component for routes with Clerk
const AppWithClerk = () => (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <BrowserRouter>
          <DragDropGuard />
          <OrganizationProvider>
            <WeatherProvider>
              <ClockProvider>
                <NotesManagerProvider>
                  <AppsManagerProvider>
                    <NotificationManagerProvider>
                      <ChatWebSocketProvider>
                      <SoundstageWebSocketProvider>
                        <OptimizedContextProvider>
                          <ClerkSupabaseSync>
                            <FocusModeSchedulerService />
                          <Toaster />
                          <Sonner />
                          <Suspense fallback={<DashboardSkeleton />}>
                          <Routes>
                            <Route
                                path="/"
                                element={
                                  <>
                                    <SignedIn>
                                      <DashboardWrapper />
                                    </SignedIn>
                                    <SignedOut>
                                      <RedirectToSignIn redirectUrl="/login" />
                                    </SignedOut>
                                  </>
                                }
                            />
                            <Route path="/login" element={<Login />} />
                            <Route
                                path="/onboarding"
                                element={
                                  <>
                                    <SignedIn>
                                      <OnboardingProtectedRoute />
                                    </SignedIn>
                                    <SignedOut>
                                      <RedirectToSignIn redirectUrl="/login" />
                                    </SignedOut>
                                  </>
                                }
                            />
                            <Route
                                path="/dashboard"
                                element={
                                  <>
                                    <SignedIn>
                                      <DashboardWrapper />
                                    </SignedIn>
                                    <SignedOut>
                                      <RedirectToSignIn redirectUrl="/login" />
                                    </SignedOut>
                                  </>
                                }
                            />
                            <Route
                                path="/notifications"
                                element={
                                  <>
                                    <SignedIn>
                                      <Notifications />
                                    </SignedIn>
                                    <SignedOut>
                                      <RedirectToSignIn redirectUrl="/login" />
                                    </SignedOut>
                                  </>
                                }
                            />
                            <Route
                                path="/lock"
                                element={
                                  <>
                                    <SignedIn>
                                      <Lock />
                                    </SignedIn>
                                    <SignedOut>
                                      <RedirectToSignIn redirectUrl="/login" />
                                    </SignedOut>
                                  </>
                                }
                            />
                            {/* Guest meeting join — no auth required */}
                            <Route
                                path="/meeting/:meetingId/guest"
                                element={<GuestMeetingJoin />}
                            />
                            {/* Meeting deep link */}
                            <Route
                                path="/meeting/:meetingId"
                                element={
                                  <>
                                    <SignedIn>
                                      <MeetingJoin />
                                    </SignedIn>
                                    <SignedOut>
                                      <RedirectToSignIn />
                                    </SignedOut>
                                  </>
                                }
                            />
                            {/* Admin Routes */}
                            <Route
                                path="/admin/roles"
                                element={
                                  <>
                                    <SignedIn>
                                      <RoleManagement />
                                    </SignedIn>
                                    <SignedOut>
                                      <RedirectToSignIn redirectUrl="/login" />
                                    </SignedOut>
                                  </>
                                }
                            />
                            <Route
                                path="/admin/users"
                                element={
                                  <>
                                    <SignedIn>
                                      <UserRoleManagement />
                                    </SignedIn>
                                    <SignedOut>
                                      <RedirectToSignIn redirectUrl="/login" />
                                    </SignedOut>
                                  </>
                                }
                            />
                            <Route
                                path="/admin/audit-log"
                                element={
                                  <>
                                    <SignedIn>
                                      <PermissionAuditLog />
                                    </SignedIn>
                                    <SignedOut>
                                      <RedirectToSignIn redirectUrl="/login" />
                                    </SignedOut>
                                  </>
                                }
                            />
                            <Route path="*" element={<NotFound />} />
                            </Routes>
                          </Suspense>
                          </ClerkSupabaseSync>
                      </OptimizedContextProvider>
                      </SoundstageWebSocketProvider>
                      </ChatWebSocketProvider>
                    </NotificationManagerProvider>
                  </AppsManagerProvider>
                </NotesManagerProvider>
              </ClockProvider>
            </WeatherProvider>
          </OrganizationProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
);

const App = () => {
  const isValidClerkKey = PUBLISHABLE_KEY && 
    PUBLISHABLE_KEY !== "YOUR_PUBLISHABLE_KEY" && 
    PUBLISHABLE_KEY.startsWith("pk_");
  
  return isValidClerkKey ? <AppWithClerk /> : <AppWithoutClerk />;
};

export default App;